
import React, { useEffect } from "react";
import { Subscription } from "@/types/subscription";
import SubscriptionCard from "../SubscriptionCard";
import { useAuth } from "@/context/AuthContext";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface SubscriptionListProps {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  subscriptions?: Subscription[];
}

export function SubscriptionList({
  isLoading,
  isError,
  error,
  subscriptions
}: SubscriptionListProps) {
  const { user, isAdmin } = useAuth();
  
  useEffect(() => {
    console.log("SubscriptionList rendering with user:", user?.email);
    console.log("SubscriptionList admin check result:", isAdmin);
    console.log("SubscriptionList data:", subscriptions?.length || 0, "subscriptions");
  }, [user, isAdmin, subscriptions]);
  
  if (!user) {
    console.log("SubscriptionList: No user, hiding component");
    return null; // Hide completely for unauthenticated users
  }
  
  // Show admin view for admin users
  if (isAdmin) {
    console.log("SubscriptionList: Admin user detected, showing all subscriptions");

    return (
      <div className="bg-white rounded-lg shadow-md p-6 border-2 border-blue-500 mt-4">
        <h2 className="text-xl font-bold mb-4 border-b pb-2">All Subscriptions (Admin Only)</h2>
        <p className="mb-4">Logged in as admin: {user?.email}</p>
        {isLoading ? (
          <div className="p-4">Loading subscriptions from Supabase...</div>
        ) : isError ? (
          <div className="p-4 text-red-600">
            <p>Error loading subscriptions: {error instanceof Error ? error.message : String(error)}</p>
            <p className="mt-2">Please check your Supabase connection or table structure.</p>
          </div>
        ) : !subscriptions || subscriptions.length === 0 ? (
          <div className="p-4 text-gray-500">
            <p>No subscriptions found in Supabase.</p>
            <p className="text-xs mt-2">Make sure your Supabase table matches the expected structure.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {subscriptions.map(sub => (
              <SubscriptionCard key={sub.id} subscription={sub} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Show user-specific view for regular users
  console.log("SubscriptionList: Regular user, directing to dashboard");
  return (
    <div className="bg-white rounded-lg shadow-md p-6 border-2 border-green-500 mt-4">
      <h2 className="text-xl font-bold mb-4">Your Subscriptions</h2>
      <p className="mb-4 text-gray-600">
        Welcome back, {user.email}! To view and manage your subscriptions, please visit your dashboard.
      </p>
      <div className="flex gap-4">
        <Button asChild>
          <Link to="/dashboard">View Dashboard</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/subscriptions">Add New Subscription</Link>
        </Button>
      </div>
    </div>
  );
}
